/**
 * DrillDownModal - FERP 4-Tab Entity Detail Explorer
 *
 * Universal drill-down component for ANY entity in lifecycle dashboard.
 * Domain-agnostic design works with all 16 primitive workflow types.
 *
 * Features:
 * - Overview Tab: Entity summary, related entities, quick actions
 * - Timeline Tab: Visual event timeline with filtering
 * - Primitives Tab: 16 primitive type composition and relationships
 * - Evidence Tab: AI generation evidence and confidence scores
 *
 * FERP Principles:
 * - Data-driven: All data from backend GraphQL (entityDetails query)
 * - Repository pattern: Uses EntityRepository for data access
 * - Domain-agnostic: Works with any entity type
 * - Life-critical: Complete audit trails for all actions
 */

import React, { useState, useEffect } from 'react';
// eslint-disable-next-line no-restricted-imports -- DATA-LAYER component, allowed to use Apollo Client directly
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import {
  Modal,
  Tabs,
  Card,
  Badge,
  Group,
  Stack,
  Text,
  Button,
  Loader,
  Alert,
  Timeline,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Grid,
  Progress,
  Divider,
  Table,
  ScrollArea,
} from '@mantine/core';
import {
  IconX,
  IconInfoCircle,
  IconClock,
  IconBuildingBank,
  IconAlertCircle,
  IconCheck,
  IconEye,
  IconEdit,
  IconTrash,
  IconCopy,
  IconDownload,
  IconChartBar,
  IconBrain,
  IconDatabase,
} from '@tabler/icons-react';

/**
 * GraphQL Query: Get entity details for drill-down modal
 *
 * FERP Principles:
 * - Domain-agnostic: Works with all 16 primitive types
 * - Data-driven: All data from backend repositories
 * - Complete audit trail: Includes timeline events
 */
const ENTITY_DETAILS_QUERY = gql`
  query EntityDetails($input: EntityDetailsInput!) {
    entityDetails(input: $input) {
      entityId
      entityType
      name
      status
      createdAt
      updatedAt
      createdBy
      metadata
      relatedEntities {
        entityId
        entityType
        name
        relationshipType
        metadata
      }
      timelineEvents {
        id
        timestamp
        eventType
        description
        userId
        metadata
      }
      primitiveComposition {
        primitiveType
        primitiveId
        configuration
      }
      evidenceAttribution {
        sourceDocumentId
        sourceDocumentName
        confidence
        extractionMetadata
        llmProvider
      }
    }
  }
`;

/**
 * Entity detail data structure (domain-agnostic)
 */
export interface EntityDetails {
  /** Entity unique identifier */
  entityId: string;

  /** Entity type from 16 primitives (WorkflowDefinition, Context, Actor, etc.) */
  entityType: string;

  /** Human-readable entity name */
  name: string;

  /** Current state or status */
  status: string;

  /** Creation timestamp */
  createdAt: number;

  /** Last update timestamp */
  updatedAt: number;

  /** Created by user ID */
  createdBy: string;

  /** Entity-specific metadata (domain-agnostic) */
  metadata: Record<string, any>;

  /** Related entities (relationships) */
  relatedEntities: RelatedEntity[];

  /** Timeline events for this entity */
  timelineEvents: TimelineEvent[];

  /** Primitive type composition */
  primitiveComposition: PrimitiveInstance[];

  /** AI evidence attribution (if AI-generated) */
  evidenceAttribution?: EvidenceAttribution;
}

export interface RelatedEntity {
  entityId: string;
  entityType: string;
  name: string;
  relationshipType: string;
}

export interface TimelineEvent {
  id: string;
  timestamp: number;
  eventType: string;
  description: string;
  userId: string;
  metadata: Record<string, any>;
}

export interface PrimitiveInstance {
  primitiveType: string;
  primitiveId: string;
  configuration: Record<string, any>;
}

export interface EvidenceAttribution {
  sourceDocumentId?: string;
  sourceDocumentName?: string;
  confidence: number;
  extractionMetadata: Record<string, any>;
  llmProvider?: string;
}

export interface DrillDownModalProps {
  /** Modal open state */
  opened: boolean;

  /** Close handler */
  onClose: () => void;

  /** Entity ID to display details for */
  entityId: string;

  /** Entity type (one of 16 primitives) */
  entityType: string;

  /** Optional: Pre-loaded entity data */
  entityData?: Partial<EntityDetails>;
}

/**
 * DrillDownModal Component - 4-Tab Entity Explorer
 */
export const DrillDownModal: React.FC<DrillDownModalProps> = ({
  opened,
  onClose,
  entityId,
  entityType,
  entityData: initialData,
}) => {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [entityDetails, setEntityDetails] = useState<EntityDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // GraphQL query for entity details (SPRINT 2 COMPLETION: 95% → 100%)
  const { data, loading: queryLoading, error: queryError, refetch } = useQuery<Record<string, any>>(ENTITY_DETAILS_QUERY, {
    variables: {
      input: {
        entityId,
        entityType,
        includeTimeline: true,
        includeRelationships: true,
        includePrimitives: true,
        includeEvidence: true,
      },
    },
    skip: !opened || !entityId, // Only query when modal is opened
  });

  // Transform backend data to frontend state
  useEffect(() => {
    if (!opened || !entityId) return;

    setLoading(queryLoading);
    setError(queryError || null);

    if (data?.entityDetails) {
      // Transform backend camelCase data to frontend EntityDetails interface
      const backendData = data.entityDetails;
      const transformedDetails: EntityDetails = {
        entityId: backendData.entityId,
        entityType: backendData.entityType,
        name: backendData.name,
        status: backendData.status,
        createdAt: backendData.createdAt,
        updatedAt: backendData.updatedAt,
        createdBy: backendData.createdBy,
        metadata: backendData.metadata || {},
        relatedEntities: backendData.relatedEntities || [],
        timelineEvents: backendData.timelineEvents || [],
        primitiveComposition: backendData.primitiveComposition || [],
        evidenceAttribution: backendData.evidenceAttribution,
      };

      setEntityDetails(transformedDetails);
    }
  }, [opened, entityId, data, queryLoading, queryError]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <ThemeIcon variant="light" color="blue" size="lg">
            <IconInfoCircle size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg">
              {entityDetails?.name || 'Entity Details'}
            </Text>
            <Text size="sm" color="dimmed">
              {entityType} • {entityId.slice(0, 11)}
            </Text>
          </div>
        </Group>
      }
      size="xl"
      padding="lg"
      closeButtonProps={{
        icon: <IconX size={20} />,
      }}
    >
      {loading ? (
        <Stack ta="center" justify="center" style={{ minHeight: 400 }}>
          <Loader size="lg" />
          <Text color="dimmed">Loading entity details...</Text>
        </Stack>
      ) : error ? (
        <Alert icon={<IconAlertCircle size={20} />} title="Error Loading Details" color="red">
          {error.message}
        </Alert>
      ) : (
        <Tabs value={activeTab} onChange={(val: string | null) => setActiveTab(val ?? 'overview')}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="timeline" leftSection={<IconClock size={16} />}>
              Timeline
            </Tabs.Tab>
            <Tabs.Tab value="primitives" leftSection={<IconDatabase size={16} />}>
              Primitives
            </Tabs.Tab>
            {entityDetails?.evidenceAttribution && (
              <Tabs.Tab value="evidence" leftSection={<IconBrain size={16} />}>
                Evidence
              </Tabs.Tab>
            )}
          </Tabs.List>

          <ScrollArea style={{ maxHeight: 600 }} mt="md">
            {/* OVERVIEW TAB */}
            <Tabs.Panel value="overview" pt="md">
              <OverviewTab entityDetails={entityDetails!} />
            </Tabs.Panel>

            {/* TIMELINE TAB */}
            <Tabs.Panel value="timeline" pt="md">
              <TimelineTab events={entityDetails?.timelineEvents || []} />
            </Tabs.Panel>

            {/* PRIMITIVES TAB */}
            <Tabs.Panel value="primitives" pt="md">
              <PrimitivesTab primitives={entityDetails?.primitiveComposition || []} />
            </Tabs.Panel>

            {/* EVIDENCE TAB (only if AI-generated) */}
            {entityDetails?.evidenceAttribution && (
              <Tabs.Panel value="evidence" pt="md">
                <EvidenceTab evidence={entityDetails.evidenceAttribution} />
              </Tabs.Panel>
            )}
          </ScrollArea>
        </Tabs>
      )}
    </Modal>
  );
};

/**
 * Overview Tab - Entity summary and related entities
 */
const OverviewTab: React.FC<{ entityDetails: EntityDetails }> = ({ entityDetails }) => {
  return (
    <Stack gap="lg">
      {/* Entity Summary Card */}
      <Card shadow="sm" p="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text fw={600} size="lg">
            Entity Summary
          </Text>
          <Badge color="green" variant="filled">
            {entityDetails.status}
          </Badge>
        </Group>

        <Grid gutter="md">
          <Grid.Col span={6}>
            <Text size="sm" color="dimmed">
              Entity ID
            </Text>
            <Text fw={500}>{entityDetails.entityId.slice(0, 11)}</Text>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" color="dimmed">
              Entity Type
            </Text>
            <Text fw={500}>{entityDetails.entityType}</Text>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" color="dimmed">
              Created At
            </Text>
            <Text fw={500}>{new Date(entityDetails.createdAt).toLocaleString()}</Text>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" color="dimmed">
              Updated At
            </Text>
            <Text fw={500}>{new Date(entityDetails.updatedAt).toLocaleString()}</Text>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" color="dimmed">
              Created By
            </Text>
            <Text fw={500}>{entityDetails.createdBy}</Text>
          </Grid.Col>
        </Grid>
      </Card>

      {/* Related Entities Card */}
      <Card shadow="sm" p="md" withBorder>
        <Text fw={600} size="lg" mb="md">
          Related Entities ({entityDetails.relatedEntities.length})
        </Text>

        {entityDetails.relatedEntities.length === 0 ? (
          <Text color="dimmed" size="sm">
            No related entities found
          </Text>
        ) : (
          <Stack gap="sm">
            {entityDetails.relatedEntities.map((related) => (
              <Group key={related.entityId} justify="space-between">
                <div>
                  <Text fw={500}>{related.name}</Text>
                  <Text size="xs" color="dimmed">
                    {related.entityType} • {related.relationshipType}
                  </Text>
                </div>
                <ActionIcon variant="light" color="blue">
                  <IconEye size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        )}
      </Card>

      {/* Quick Actions */}
      <Group justify="space-between">
        <Button leftSection={<IconEdit size={16} />} variant="light">
          Edit Entity
        </Button>
        <Button leftSection={<IconCopy size={16} />} variant="light">
          Duplicate
        </Button>
        <Button leftSection={<IconDownload size={16} />} variant="light">
          Export
        </Button>
        <Button leftSection={<IconTrash size={16} />} color="red" variant="light">
          Delete
        </Button>
      </Group>
    </Stack>
  );
};

/**
 * Timeline Tab - Visual event timeline
 */
const TimelineTab: React.FC<{ events: TimelineEvent[] }> = ({ events }) => {
  return (
    <Stack gap="lg">
      <Text fw={600} size="lg">
        Event Timeline ({events.length} events)
      </Text>

      {events.length === 0 ? (
        <Alert icon={<IconInfoCircle size={20} />} title="No Events" color="blue">
          No timeline events recorded for this entity yet.
        </Alert>
      ) : (
        <Timeline active={events.length} lineWidth={2}>
          {events.map((event, index) => (
            <Timeline.Item
              key={event.id}
              bullet={<IconCheck size={12} />}
              title={event.description}
            >
              <Text color="dimmed" size="sm">
                {new Date(event.timestamp).toLocaleString()}
              </Text>
              <Text size="xs" mt={4} color="dimmed">
                By: {event.userId} • Type: {event.eventType}
              </Text>
            </Timeline.Item>
          ))}
        </Timeline>
      )}
    </Stack>
  );
};

/**
 * Primitives Tab - 16 Primitive Type Composition
 */
const PrimitivesTab: React.FC<{ primitives: PrimitiveInstance[] }> = ({ primitives }) => {
  return (
    <Stack gap="lg">
      <Text fw={600} size="lg">
        Primitive Composition ({primitives.length} primitives)
      </Text>

      {primitives.length === 0 ? (
        <Alert icon={<IconInfoCircle size={20} />} title="No Primitives" color="blue">
          This entity has no primitive composition data available.
        </Alert>
      ) : (
        <Table highlightOnHover>
          <thead>
            <tr>
              <th>Primitive Type</th>
              <th>Primitive ID</th>
              <th>Configuration</th>
            </tr>
          </thead>
          <tbody>
            {primitives.map((primitive, index) => (
              <tr key={index}>
                <td>
                  <Badge variant="filled">{primitive.primitiveType}</Badge>
                </td>
                <td>
                  <Text size="sm" ff="monospace">
                    {primitive.primitiveId}
                  </Text>
                </td>
                <td>
                  <Text size="sm" color="dimmed">
                    {Object.keys(primitive.configuration).length} fields
                  </Text>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Stack>
  );
};

/**
 * Evidence Tab - AI Generation Evidence and Confidence
 */
const EvidenceTab: React.FC<{ evidence: EvidenceAttribution }> = ({ evidence }) => {
  const confidenceColor =
    evidence.confidence >= 0.9 ? 'green' : evidence.confidence >= 0.7 ? 'yellow' : 'red';

  return (
    <Stack gap="lg">
      <Card shadow="sm" p="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text fw={600} size="lg">
            AI Generation Evidence
          </Text>
          <Badge color={confidenceColor} variant="filled">
            {(evidence.confidence * 100).toFixed(1)}% Confidence
          </Badge>
        </Group>

        <Progress
          value={evidence.confidence * 100}
          color={confidenceColor}
          size="lg"
          mb="md"
        />

        {evidence.sourceDocumentName && (
          <>
            <Text size="sm" color="dimmed">
              Source Document
            </Text>
            <Text fw={500} mb="md">
              {evidence.sourceDocumentName}
            </Text>
          </>
        )}

        {evidence.llmProvider && (
          <>
            <Text size="sm" color="dimmed">
              LLM Provider
            </Text>
            <Text fw={500} mb="md">
              {evidence.llmProvider}
            </Text>
          </>
        )}

        <Divider my="md" />

        <Text fw={600} mb="md">
          Extraction Metadata
        </Text>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#f5f5f5',
            padding: '12px',
            borderRadius: '8px',
            overflow: 'auto',
            maxHeight: '300px',
          }}
        >
          {JSON.stringify(evidence.extractionMetadata, null, 2)}
        </pre>
      </Card>
    </Stack>
  );
};

export default DrillDownModal;
